import React, { useState, useEffect } from 'react';
import {
  Card, Button, Radio, Space, Alert, Typography, message, Table, Tag, Modal, Input,
  Form, Switch, Select, InputNumber, Upload, Row, Col, Statistic, Popconfirm, Tooltip
} from 'antd';
import {
  CloudDownloadOutlined, DatabaseOutlined, SaveOutlined, ReloadOutlined,
  DeleteOutlined, UndoOutlined, UploadOutlined, SettingOutlined, TeamOutlined,
  UserOutlined, ReadOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Paragraph, Text } = Typography;
const CONFIRM_PHRASE = 'RESTORE';

function triggerBlobDownload(res, fallbackName) {
  const disposition = res.headers['content-disposition'];
  const match = disposition && /filename="?([^"]+)"?/.exec(disposition);
  const filename = match ? match[1] : fallbackName;

  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/gzip' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function BackupManager() {
  const [format, setFormat] = useState('archive'); // برای دانلود فوری
  const [downloading, setDownloading] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [creatingServerBackup, setCreatingServerBackup] = useState(false);
  const [downloadingFilename, setDownloadingFilename] = useState(null);

  const [settings, setSettings] = useState({ autoBackupEnabled: false, backupHour: 3, retentionDays: 30, format: 'archive' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [restoreServerTarget, setRestoreServerTarget] = useState(null); // backup meta
  const [restoreServerPhrase, setRestoreServerPhrase] = useState('');
  const [restoringServer, setRestoringServer] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFormat, setUploadFormat] = useState('archive');
  const [uploadPhrase, setUploadPhrase] = useState('');
  const [restoringUpload, setRestoringUpload] = useState(false);

  useEffect(() => {
    loadStats();
    loadBackups();
    loadSettings();
  }, []);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await adminAPI.getBackupStats();
      if (res.data.success) setStats(res.data.stats);
    } catch (error) {
      message.error('خطا در بارگذاری آمار دیتابیس');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await adminAPI.listServerBackups();
      if (res.data.success) setBackups(res.data.backups);
    } catch (error) {
      message.error('خطا در بارگذاری لیست بکاپ‌های سرور');
    } finally {
      setBackupsLoading(false);
    }
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await adminAPI.getBackupSettings();
      if (res.data.success && res.data.config) {
        setSettings({ autoBackupEnabled: false, backupHour: 3, retentionDays: 30, format: 'archive', ...res.data.config.value });
      }
    } catch (error) {
      // اگر تنظیمات هنوز ساخته نشده، مقدار پیش‌فرض همان‌جوری که در state هست باقی می‌ماند
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleQuickDownload = async () => {
    setDownloading(true);
    const hide = message.loading('در حال تهیه بکاپ دیتابیس… لطفاً صبر کنید', 0);
    try {
      const res = await adminAPI.downloadDatabaseBackup(format === 'json' ? 'json' : null);

      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const errText = await res.data.text();
        let msg = 'خطا در تهیه بکاپ';
        try { msg = JSON.parse(errText).message || msg; } catch (_) {}
        throw new Error(msg);
      }

      triggerBlobDownload(res, `backup-${new Date().toISOString().replace(/[:.]/g, '-')}.gz`);
      message.success('بکاپ با موفقیت دانلود شد');
    } catch (error) {
      message.error(error.message || 'خطا در تهیه بکاپ دیتابیس');
    } finally {
      hide();
      setDownloading(false);
    }
  };

  const handleCreateServerBackup = async () => {
    setCreatingServerBackup(true);
    const hide = message.loading('در حال ساخت و ذخیره بکاپ روی سرور… ممکن است کمی طول بکشد', 0);
    try {
      const res = await adminAPI.createServerBackup();
      if (res.data.success) {
        message.success('بکاپ روی سرور ذخیره شد');
        loadBackups();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در ذخیره بکاپ روی سرور');
    } finally {
      hide();
      setCreatingServerBackup(false);
    }
  };

  const handleDownloadServerBackup = async (record) => {
    setDownloadingFilename(record.filename);
    try {
      const res = await adminAPI.downloadServerBackup(record.filename);
      triggerBlobDownload(res, record.filename);
    } catch (error) {
      message.error('خطا در دانلود فایل بکاپ');
    } finally {
      setDownloadingFilename(null);
    }
  };

  const handleDeleteServerBackup = async (record) => {
    try {
      const res = await adminAPI.deleteServerBackup(record.filename);
      if (res.data.success) {
        message.success('بکاپ حذف شد');
        loadBackups();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در حذف بکاپ');
    }
  };

  const submitRestoreServer = async () => {
    if (!restoreServerTarget) return;
    if (restoreServerPhrase !== CONFIRM_PHRASE) {
      message.error(`عبارت تاییدی را دقیقاً «${CONFIRM_PHRASE}» وارد کنید`);
      return;
    }
    setRestoringServer(true);
    const hide = message.loading('در حال بازیابی دیتابیس… تا اتمام این عملیات از این صفحه خارج نشوید', 0);
    try {
      const res = await adminAPI.restoreFromServerBackup(restoreServerTarget.filename, restoreServerPhrase);
      if (res.data.success) {
        message.success('دیتابیس با موفقیت بازیابی شد');
        setRestoreServerTarget(null);
        setRestoreServerPhrase('');
        loadStats();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در بازیابی دیتابیس');
    } finally {
      hide();
      setRestoringServer(false);
    }
  };

  const submitRestoreUpload = async () => {
    if (!uploadFile) {
      message.error('یک فایل بکاپ انتخاب کنید');
      return;
    }
    if (uploadPhrase !== CONFIRM_PHRASE) {
      message.error(`عبارت تاییدی را دقیقاً «${CONFIRM_PHRASE}» وارد کنید`);
      return;
    }
    setRestoringUpload(true);
    const hide = message.loading('در حال بازیابی دیتابیس از فایل… تا اتمام این عملیات از این صفحه خارج نشوید', 0);
    try {
      const res = await adminAPI.restoreFromUpload(uploadFile, uploadPhrase, uploadFormat);
      if (res.data.success) {
        message.success('دیتابیس با موفقیت از فایل بازیابی شد');
        setUploadFile(null);
        setUploadPhrase('');
        loadStats();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در بازیابی دیتابیس از فایل');
    } finally {
      hide();
      setRestoringUpload(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await adminAPI.updateBackupSettings(settings);
      if (res.data.success) message.success('تنظیمات بکاپ خودکار ذخیره شد');
    } catch (error) {
      message.error('خطا در ذخیره تنظیمات');
    } finally {
      setSavingSettings(false);
    }
  };

  const statCards = stats && (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col xs={12} sm={8} md={4}><Statistic title="کاربران" value={stats.totalUsers} prefix={<UserOutlined />} /></Col>
      <Col xs={12} sm={8} md={4}><Statistic title="جادوگرها" value={stats.totalWizards} /></Col>
      <Col xs={12} sm={8} md={4}><Statistic title="اساتید" value={stats.totalTeachers} prefix={<ReadOutlined />} /></Col>
      <Col xs={12} sm={8} md={4}><Statistic title="مدیران" value={stats.totalAdmins} /></Col>
      <Col xs={12} sm={8} md={4}><Statistic title="لیست انتظار" value={stats.waitlistTotal} prefix={<TeamOutlined />} /></Col>
      <Col xs={12} sm={8} md={4}><Statistic title="دعوت‌نامه‌ها" value={stats.totalInvitations} /></Col>
    </Row>
  );

  const backupColumns = [
    {
      title: 'تاریخ و ساعت',
      render: (_, r) => (
        <span>
          <ClockCircleOutlined style={{ marginInlineEnd: 6 }} />
          {new Date(r.createdAt).toLocaleString('fa-IR')}
        </span>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: 'descend'
    },
    {
      title: 'نوع',
      dataIndex: 'trigger',
      render: (t) => (t === 'auto' ? <Tag color="purple">خودکار</Tag> : <Tag color="cyan">دستی</Tag>)
    },
    {
      title: 'فرمت',
      dataIndex: 'format',
      render: (f) => (f === 'archive' ? <Tag color="green">mongodump</Tag> : <Tag color="orange">JSON</Tag>)
    },
    { title: 'حجم', dataIndex: 'sizeHuman' },
    {
      title: 'وضعیت دیتابیس در آن لحظه',
      render: (_, r) => r.stats ? (
        <Tooltip title="تعداد کاربر / استاد / لیست انتظار در لحظه‌ی ساخت این بکاپ">
          <span style={{ direction: 'ltr' }}>
            👤 {r.stats.totalUsers} · 🎓 {r.stats.totalTeachers} · ⏳ {r.stats.waitlistTotal}
          </span>
        </Tooltip>
      ) : '-'
    },
    {
      title: 'عملیات',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<CloudDownloadOutlined />}
            loading={downloadingFilename === record.filename}
            onClick={() => handleDownloadServerBackup(record)}
          >
            دانلود
          </Button>
          <Button
            size="small"
            icon={<UndoOutlined />}
            onClick={() => { setRestoreServerTarget(record); setRestoreServerPhrase(''); }}
          >
            بازیابی
          </Button>
          <Popconfirm title="این بکاپ حذف شود؟" okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }} onConfirm={() => handleDeleteServerBackup(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ fontFamily: "'Vazirmatn', sans-serif" }}>

      <Card title={<span style={{ fontWeight: 700 }}><DatabaseOutlined style={{ marginLeft: 8, color: '#722f37' }} />وضعیت فعلی دیتابیس</span>} style={{ marginBottom: 16 }} loading={statsLoading} extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadStats}>تازه‌سازی</Button>}>
        {statCards || <Text type="secondary">در حال بارگذاری…</Text>}
      </Card>

      <Card
        title={<span style={{ fontWeight: 700 }}><CloudDownloadOutlined style={{ marginLeft: 8, color: '#722f37' }} />دانلود بکاپ فوری</span>}
        style={{ marginBottom: 16 }}
      >
        <Paragraph>
          یک نسخهٔ پشتیبان کامل از کل دیتابیس تهیه کرده و مستقیماً در مرورگر دانلود می‌کنید (بدون ذخیره روی سرور).
        </Paragraph>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)}>
            <Space direction="vertical">
              <Radio value="archive">آرشیو بومی MongoDB — mongorestore --archive --gzip (پیشنهادی)</Radio>
              <Radio value="json">خروجی JSON فشرده (fallback خودکار اگر mongodump نصب نباشد)</Radio>
            </Space>
          </Radio.Group>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            loading={downloading}
            onClick={handleQuickDownload}
            style={{ background: '#722f37', borderColor: '#722f37' }}
          >
            تهیه و دانلود بکاپ
          </Button>
        </Space>
      </Card>

      <Card
        title={<span style={{ fontWeight: 700 }}><SaveOutlined style={{ marginLeft: 8, color: '#722f37' }} />بکاپ‌های ذخیره‌شده روی سرور</span>}
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadBackups}>تازه‌سازی</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={creatingServerBackup} onClick={handleCreateServerBackup}>
              ذخیره بکاپ جدید روی سرور
            </Button>
          </Space>
        }
      >
        <Table
          columns={backupColumns}
          dataSource={backups}
          rowKey="filename"
          loading={backupsLoading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'هنوز بکاپی روی سرور ذخیره نشده است' }}
        />
      </Card>

      <Card
        title={<span style={{ fontWeight: 700 }}><SettingOutlined style={{ marginLeft: 8, color: '#722f37' }} />بکاپ خودکار شبانه</span>}
        style={{ marginBottom: 16, maxWidth: 640 }}
        loading={settingsLoading}
      >
        <Form layout="vertical">
          <Form.Item label="بکاپ خودکار شبانه فعال باشد">
            <Switch checked={settings.autoBackupEnabled} onChange={(v) => setSettings((s) => ({ ...s, autoBackupEnabled: v }))} />
          </Form.Item>
          <Form.Item label="ساعت اجرای بکاپ خودکار (به وقت سرور)">
            <Select
              value={settings.backupHour}
              onChange={(v) => setSettings((s) => ({ ...s, backupHour: v }))}
              options={Array.from({ length: 24 }, (_, h) => ({ label: `${h.toString().padStart(2, '0')}:۰۰`, value: h }))}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item label="نگهداری بکاپ‌های روی سرور (روز) — بعد از این مدت خودکار پاک می‌شوند">
            <InputNumber
              min={0}
              value={settings.retentionDays}
              onChange={(v) => setSettings((s) => ({ ...s, retentionDays: v }))}
              style={{ width: 200 }}
              addonAfter="روز"
            />
            <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>عدد ۰ یعنی بدون محدودیت زمانی (هرگز خودکار پاک نشود)</div>
          </Form.Item>
          <Form.Item label="فرمت بکاپ خودکار">
            <Select
              value={settings.format}
              onChange={(v) => setSettings((s) => ({ ...s, format: v }))}
              options={[{ label: 'آرشیو بومی MongoDB (پیشنهادی)', value: 'archive' }, { label: 'JSON', value: 'json' }]}
              style={{ width: 320 }}
            />
          </Form.Item>
          <Button type="primary" icon={<SaveOutlined />} loading={savingSettings} onClick={saveSettings}>
            ذخیره تنظیمات
          </Button>
        </Form>
      </Card>

      <Card
        title={<span style={{ fontWeight: 700 }}><UploadOutlined style={{ marginLeft: 8, color: '#a8071a' }} />بازیابی دیتابیس از فایل</span>}
        style={{ marginBottom: 16, maxWidth: 640 }}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="هشدار جدی"
          description="این عملیات تمام داده‌های فعلی دیتابیس را با محتوای فایل بکاپ جایگزین می‌کند و برگشت‌پذیر نیست، مگر اینکه از قبل بکاپ گرفته باشید."
        />
        <Form layout="vertical">
          <Form.Item label="فایل بکاپ (.archive.gz یا .json.gz)">
            <Upload
              beforeUpload={(file) => { setUploadFile(file); return false; }}
              onRemove={() => setUploadFile(null)}
              fileList={uploadFile ? [uploadFile] : []}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
            </Upload>
          </Form.Item>
          <Form.Item label="فرمت فایل">
            <Select
              value={uploadFormat}
              onChange={setUploadFormat}
              options={[{ label: 'آرشیو بومی MongoDB (mongodump)', value: 'archive' }, { label: 'JSON', value: 'json' }]}
              style={{ width: 320 }}
            />
          </Form.Item>
          <Form.Item label={`برای تایید، عبارت «${CONFIRM_PHRASE}» را دقیقاً وارد کنید`}>
            <Input value={uploadPhrase} onChange={(e) => setUploadPhrase(e.target.value)} style={{ direction: 'ltr', maxWidth: 240 }} placeholder={CONFIRM_PHRASE} />
          </Form.Item>
          <Button
            danger
            type="primary"
            icon={<UndoOutlined />}
            loading={restoringUpload}
            disabled={!uploadFile || uploadPhrase !== CONFIRM_PHRASE}
            onClick={submitRestoreUpload}
          >
            بازیابی دیتابیس از این فایل
          </Button>
        </Form>
      </Card>

      <Alert
        type="warning"
        showIcon
        style={{ maxWidth: 720 }}
        message="نکتهٔ امنیتی"
        description="فایل‌های بکاپ حاوی تمام اطلاعات کاربران هستند. آن‌ها را در محل امن نگهداری کنید و در اختیار افراد غیرمجاز قرار ندهید."
      />

      <Modal
        title={restoreServerTarget ? `بازیابی از بکاپ — ${new Date(restoreServerTarget.createdAt).toLocaleString('fa-IR')}` : ''}
        open={!!restoreServerTarget}
        onCancel={() => { setRestoreServerTarget(null); setRestoreServerPhrase(''); }}
        onOk={submitRestoreServer}
        okText="بازیابی دیتابیس"
        okButtonProps={{ danger: true, disabled: restoreServerPhrase !== CONFIRM_PHRASE }}
        cancelText="انصراف"
        confirmLoading={restoringServer}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="این عملیات دیتابیس فعلی را کاملاً با این بکاپ جایگزین می‌کند و برگشت‌پذیر نیست."
        />
        <div style={{ marginBottom: 12 }}>{`برای تایید، عبارت «${CONFIRM_PHRASE}» را دقیقاً وارد کنید:`}</div>
        <Input value={restoreServerPhrase} onChange={(e) => setRestoreServerPhrase(e.target.value)} style={{ direction: 'ltr' }} placeholder={CONFIRM_PHRASE} />
      </Modal>
    </div>
  );
}
